import * as React from "react";
import Partner from "../../../classes/Partner";
import { ContentType, HttpVerb, requestWithAuth } from "../../../utils/httpRequest";
import PartnerData from "../../../classes/Partner";
import Lead from "../../../classes/Lead";
import api from "../../api";
import ContactList from "../Contact/ContactList/ContactList";
import AppContext from "../AppContext";
import { Spinner, SpinnerSize } from "office-ui-fabric-react";
import { OdooTheme } from "../../../utils/Themes";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextField } from "office-ui-fabric-react/lib-amd";
import { _t } from "../../../utils/Translator";
import "./Search.css";

const MAX_PARTNERS = 30;

type SearchProps = {
  query?: string;
  canCreatePartner: boolean;
  onPartnerClick: (partner: Partner, historyState?: SearchState) => void;
  onLeadClick: (lead: Lead, historyState?: SearchState) => void;
  historyState?: any;
};

export type SearchState = {
  query: string;
  foundPartners?: Partner[];
  foundLeads?: Lead[];
  canSearchLeads?: boolean;
  isLoading: boolean;
};

class Search extends React.Component<SearchProps, SearchState> {
  constructor(props, context) {
    super(props, context);
    if (props.historyState) {
      this.state = { ...props.historyState };
    } else {
      this.state = {
        query: props.query || "",
        foundPartners: undefined,
        foundLeads: undefined,
        canSearchLeads: true,
        isLoading: false
      };
    }
  }

  private cancelOnGoingRequest = () => {
    if (this.onGoingRequest) this.onGoingRequest.cancel();
  };

  private getCurrentHistoryState = () => {
    if (this.state.isLoading) {
      return {
        query: this.state.query,
        foundPartners: undefined,
        foundLeads: undefined,
        canSearchLeads: this.state.canSearchLeads,
        isLoading: true
      } as SearchState;
    } else {
      return { ...this.state } as SearchState;
    }
  };

  private getPartnersRequest = (query: String) => {
    this.setState({ isLoading: true });
    this.cancelOnGoingRequest();
    if (query.length > 0) {
      const normalizeBoolean = (value: any): boolean => {
        if (value === true || value === "True" || value === "true" || value === 1) return true;
        if (value === false || value === "False" || value === "false" || value === 0) return false;
        return Boolean(value);
      };

      const partnerRequest = requestWithAuth(
        HttpVerb.PATCH,
        api.baseURL + api.searchPartner,
        ContentType.Json,
        { args: [[["display_name", "ilike", query]]] }
      );

      const leadsRequest = requestWithAuth(
        HttpVerb.PATCH,
        api.baseURL + api.searchLeads,
        ContentType.Json,
        { args: [["|", ["name", "ilike", query], ["email_from", "ilike", query]]] }
      );

      this.onGoingRequest = {
        promise: Promise.all([partnerRequest.promise, leadsRequest.promise.catch(() => null)]),
        cancel: () => {
          partnerRequest.cancel();
          leadsRequest.cancel();
        }
      };

      this.context.addRequestCanceller(this.onGoingRequest.cancel);

      this.onGoingRequest.promise
        .then(([partnerResponse, leadsResponse]) => {
          const parsedPartners = JSON.parse(partnerResponse.responseText);
          const isPartnerSuccess = normalizeBoolean(parsedPartners.success);
          if (!isPartnerSuccess && parsedPartners.error) {
            this.context.showHttpErrorMessage(parsedPartners);
            this.setState({ foundPartners: [], foundLeads: [], isLoading: false });
            return;
          }

          const partners = isPartnerSuccess && Array.isArray(parsedPartners.data)
            ? parsedPartners.data.map(partner_json => PartnerData.fromJSON(partner_json))
            : [];

          let leads: Lead[] = [];
          let canSearchLeads = true;
          if (leadsResponse === null) {
            canSearchLeads = false;
          } else {
            try {
              const parsedLeads = JSON.parse(leadsResponse.responseText);
              const isLeadsSuccess = normalizeBoolean(parsedLeads.success);
              if (isLeadsSuccess && Array.isArray(parsedLeads.data)) {
                leads = parsedLeads.data.map(lead_json => Lead.fromJSON(lead_json));
              } else {
                canSearchLeads = false;
              }
            } catch (error) {
              canSearchLeads = false;
            }
          }

          this.setState({
            foundPartners: partners,
            foundLeads: leads,
            canSearchLeads,
            isLoading: false
          });
        })
        .catch((error) => {
          console.error("Search error:", error);
          this.context.showHttpErrorMessage(error);
          this.setState({ foundPartners: [], foundLeads: [], isLoading: false });
        });
    } else {
      this.setState({ foundPartners: [], foundLeads: [], isLoading: false });
    }
  };

  private onKeyDown = event => {
    if (event.key == "Enter") this.getPartnersRequest(this.state.query);
  };

  private onGoingRequest: { promise: Promise<any>; cancel: () => void };

  private onPartnerClick = partner => {
    this.props.onPartnerClick(partner, this.getCurrentHistoryState());
  };

  private onLeadClick = (lead: Lead) => {
    this.props.onLeadClick(lead, this.getCurrentHistoryState());
  };

  private onQueryChanged = event => {
    this.setState({ query: event.target.value });
  };

  render() {
    const searchBar = (
      <div className="search-container">
        <TextField
          className="input-search"
          placeholder={_t("Search contact in Odoo...")}
          onChange={this.onQueryChanged}
          value={this.state.query}
          onKeyDown={this.onKeyDown}
          onFocus={e => e.target.select()}
          autoFocus
        />
        <div className="odoo-muted-button search-icon" onClick={() => this.getPartnersRequest(this.state.query)}>
          <FontAwesomeIcon icon={faSearch} />
        </div>
      </div>
    );

    let resultsView = null;
    if (this.state.isLoading) {
      resultsView = (
        <div className="section-card search-spinner">
          <Spinner theme={OdooTheme} size={SpinnerSize.large} style={{ margin: "auto" }} />
        </div>
      );
    } else if (this.state.foundPartners !== undefined) {
      let foundContactsNumberText =
        this.state.foundPartners.length >= MAX_PARTNERS ? "30+" : this.state.foundPartners.length.toString();
      resultsView = (
        <div>
          <div className="section-card">
            <div className="section-top">
              <div className="custom-section-title-container">
                <div className="section-title-text custom">
                  {_t("Contacts Found (%(count)s)", {
                    count: foundContactsNumberText
                  })}
                </div>
              </div>
            </div>
            <div className="section-content">
              <ContactList
                partners={this.state.foundPartners}
                canCreatePartner={this.props.canCreatePartner}
                onItemClick={this.onPartnerClick}
              />
            </div>
          </div>

          {this.state.canSearchLeads && this.state.foundLeads !== undefined && this.state.foundLeads.length > 0 && (
            <div className="section-card">
              <div className="section-top">
                <div className="custom-section-title-container">
                  <div className="section-title-text custom">
                    {_t("Leads Found (%(count)s)", {
                      count: this.state.foundLeads.length.toString()
                    })}
                  </div>
                </div>
              </div>
              <div className="section-content">
                <div>
                  {this.state.foundLeads.map(lead => (
                    <div key={lead.id} className="search-lead-item" onClick={() => this.onLeadClick(lead)}>
                      <div className="search-lead-row">
                        <div className="search-lead-name">{lead.name}</div>
                        <span className={`search-lead-badge ${lead.type === "opportunity" ? "opportunity" : "lead"}`}>
                          {lead.type === "opportunity" ? _t("Opportunity") : _t("Lead")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        {searchBar}
        {resultsView}
      </div>
    );
  }
}

Search.contextType = AppContext;

export default Search;
